import { lazy, type ComponentType } from 'react'

import { DialogType as DialogTypeEnum } from '@/state/dialog/dialogTypes'

const CHUNK_RELOAD_KEY = 'dialogChunkReloaded'

// React.lazy caches a rejected import forever, so a chunk that fails to download
// (stale deploy, dropped connection) would leave the page stuck on an error screen.
// Reloading once recovers and lets the normal auth redirect run.
function lazyDialog<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>,
>(load: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      const dialogModule = await load()
      sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      return dialogModule
    } catch (error) {
      if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) throw error

      sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true')
      window.location.reload()
      return new Promise<never>(() => {})
    }
  })
}

const AddNoteDialog = lazyDialog(
  () => import('@/components/dialogs/NoteDialogs/AddNoteDialog'),
)
const ViewNoteDialog = lazyDialog(
  () => import('@/components/dialogs/NoteDialogs/ViewNoteDialog'),
)
const VerificationFormIsDirtyDialog = lazyDialog(
  () =>
    import('@/components/dialogs/VerificationDialogs/VerificationFormIsDirtyDialog'),
)
const OptOutTextMessageModal = lazyDialog(
  () => import('@/components/dialogs/OptOutSmsNotificationsModal'),
)
const AccountInfoModal = lazyDialog(
  () => import('@/components/dialogs/AccountInfoModal'),
)

const ErrorDialog = lazyDialog(() => import('@/components/dialogs/ErrorDialog'))

export const dialogComponents = {
  [DialogTypeEnum.ADD_NOTE]: AddNoteDialog,
  [DialogTypeEnum.VIEW_NOTE]: ViewNoteDialog,
  [DialogTypeEnum.VERIFICATION_FORM_IS_DIRTY]: VerificationFormIsDirtyDialog,
  [DialogTypeEnum.OPT_OUT_TEXT_MESSAGE_MODAL]: OptOutTextMessageModal,
  [DialogTypeEnum.ACCOUNT_INFO_MODAL]: AccountInfoModal,
  [DialogTypeEnum.GENERIC_ERROR]: ErrorDialog,
  [DialogTypeEnum.SMS_PREFERENCE_UPDATE_FAILED_ERROR]: ErrorDialog,
  [DialogTypeEnum.PACKAGE_PREVIEW_UNAVAILABLE_ERROR]: ErrorDialog,
  [DialogTypeEnum.PACKAGE_DOWNLOAD_FAILED_ERROR]: ErrorDialog,
  [DialogTypeEnum.OPT_OUT_SMS_FAILED_ERROR]: ErrorDialog,
  [DialogTypeEnum.VERIFY_INFO_SAVE_FAILED_ERROR]: ErrorDialog,
}
